import { Button, EmptyState, Field, Input, LabelCaps, SkeletonRows } from '@pm/ui';
import { CalendarOff, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtDate } from '@/lib/format';
import { useHolidays, useInvalidating, wfKeys } from '@/lib/queries';
import { useToast } from '@/lib/toast';

/** SPEC §21 / RULE-025 — feriados da organização (afetam SLA em dias úteis). */
export function HolidaysSection() {
  const toast = useToast();
  const { can } = useAuth();
  const { data = [], isLoading } = useHolidays();
  const [date, setDate] = useState('');
  const [name, setName] = useState('');
  const create = useInvalidating(
    (input: { date: string; name: string }) => api.post('/holidays', input),
    [wfKeys.holidays],
  );
  const remove = useInvalidating((id: string) => api.delete(`/holidays/${id}`), [wfKeys.holidays]);
  const manage = can('settings.manage');

  return (
    <div className="grid gap-gap-lg lg:grid-cols-[1fr_320px]">
      <div className="rounded-md border border-border bg-surface-1">
        {isLoading ? (
          <SkeletonRows rows={3} />
        ) : data.length === 0 ? (
          <EmptyState
            icon={CalendarOff}
            title="Nenhum feriado"
            description="Sem feriados cadastrados, só sábado e domingo são ignorados nos SLAs."
          />
        ) : (
          <ul>
            {data.map((h) => (
              <li
                key={h.id}
                className="flex h-row items-center gap-3 border-b border-border px-4 last:border-b-0"
              >
                <span className="font-mono text-sm">{fmtDate(`${h.date}T12:00:00`)}</span>
                <span className="font-mono text-xs text-text-3">{h.date}</span>
                <span className="flex-1">{h.name}</span>
                {manage ? (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Remover"
                    onClick={() => remove.mutate(h.id, { onError: (e) => toast.error(e) })}
                  >
                    <Trash2 />
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
      {manage ? (
        <form
          className="flex flex-col gap-3 self-start rounded-md border border-border bg-surface-1 p-4"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate(
              { date, name },
              {
                onSuccess: () => (setDate(''), setName(''), toast.success('Feriado cadastrado')),
                onError: (err) => toast.error(err),
              },
            );
          }}
        >
          <LabelCaps>Novo feriado</LabelCaps>
          <Field label="Data" htmlFor="hd-date">
            <Input
              id="hd-date"
              type="date"
              mono
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </Field>
          <Field label="Nome" htmlFor="hd-name">
            <Input
              id="hd-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex.: Natal"
            />
          </Field>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            loading={create.isPending}
            disabled={!date || !name.trim()}
          >
            <Plus /> Cadastrar
          </Button>
        </form>
      ) : null}
    </div>
  );
}
